<template>
    <div class="terms-modal" @click="closeOnOverlayClick">
      <!-- Prevent clicks on modal content from closing the modal -->
      <div class="modal-content" @click.stop>
        <h1>Terms and Conditions</h1>
  
        <p>
          <strong>Effective Date:</strong> Nov 29, 2024<br>
          <strong>Last Updated:</strong> Nov 29, 2024
        </p>
  
        <h2>Table of Contents</h2>
        <ol>
          <li><a href="#section1">General Terms of Service</a></li>
          <li><a href="#section2">Service Availability and Performance</a></li>
          <li><a href="#section3">Data Collection and Use</a></li>
          <li><a href="#section4">Hunting Application Processing</a></li>
          <li><a href="#section5">Refund Policy</a></li>
          <li><a href="#section6">Limitation of Liability</a></li>
          <li><a href="#section7">User Responsibilities</a></li>
          <li><a href="#section8">Indemnification</a></li>
          <li><a href="#section9">Dispute Resolution</a></li>
          <li><a href="#section10">Intellectual Property</a></li>
          <li><a href="#section11">Disclaimer of Warranties</a></li>
          <li><a href="#section12">Termination</a></li>
          <li><a href="#section13">Severability</a></li>
          <li><a href="#section14">Force Majeure</a></li>
          <li><a href="#section15">Entire Agreement</a></li>
        </ol>

            <h2 id="section1">1. General Terms of Service</h2>

            <h3>1.1 Acceptance of Terms</h3>
            <p>By creating an account, accessing, or using the HuntApply application and services, you acknowledge that
                you have read, understood, and agree to these Terms and Conditions.</p>

            <h3>1.2 Amendments</h3>
            <p>HuntApply reserves the right to modify these Terms and Conditions at any time. Updated terms will be
                posted in the app or on our website. Acceptance of updated terms is required for continued use of
                services provided by HuntApply.</p>

            <hr>

            <h2 id="section2">2. Service Availability and Performance</h2>

            <h3>2.1 Service Availability</h3>
            <p>While HuntApply makes its best efforts to maintain system uptime, performance, and availability, we do
                not guarantee uninterrupted or error-free operation of the application or services. Technical problems,
                both within and outside of HuntApply's control, may result in extended downtime, errors, or
                interruptions.</p>

            <h3>2.2 No Guarantees of Results</h3>
            <p>HuntApply provides tools to help you apply for hunting permits in multiple states. However, HuntApply
                does not guarantee:</p>
            <ul>
                <li>That the system will be available 24/7.</li>
                <li>That customers will be awarded a permit, even if draw odds calculations displayed in the app are
                    close to or reaching 100%.</li>
                <li>The accuracy or timeliness of notifications, including but not limited to those sent via email,
                    text, or in-app alerts.</li>
                <li>That the draw points displayed in the app are accurate or up-to-date, as point data may depend on
                    external sources that can change or become unavailable at any time.</li>
            </ul>

            <h3>2.3 Authorization to Access State Systems</h3>
            <p>By using HuntApply, you explicitly grant HuntApply the authority to act on your behalf to access state
                systems for the purpose of obtaining data needed to facilitate hunting applications. This includes, but
                is not limited to, logging into state portals, retrieving point balances, and submitting application
                information.</p>
            <p>If you are using HuntApply to assist another individual with managing their hunting applications, you
                represent and warrant that you have explicit written authorization from that individual to:</p>
            <ol>
                <li>Use HuntApply to manage their hunting applications.</li>
                <li>Access their state system accounts and retrieve any associated data.</li>
            </ol>
            <p>HuntApply reserves the right to request proof of such authorization at any time and may suspend or
                terminate your access to the services if such proof is not provided upon request.</p>

            <hr>

            <h2 id="section3">3. Data Collection and Use</h2>

            <h3>3.1 Data Submission and Use</h3>
            <p>You agree that any data you input into HuntApply, including sensitive data such as state-specific hunting
                credentials, personally identifying information, and credit card information, will be collected, stored,
                and used by HuntApply to act on your behalf for logging into state systems.</p>

            <h3>3.2 Data Security</h3>
            <p>HuntApply uses industry-standard encryption algorithms to encrypt sensitive data at rest. While HuntApply
                employs robust security measures, no system is completely secure, and we cannot guarantee that
                unauthorized access, hacking, or data breaches will not occur. By using HuntApply, you acknowledge and
                accept this risk.</p>

            <h3>3.3 Outfitter Services Disclaimer</h3>
            <p>HuntApply may provide connections to hunting outfitters or guides upon your request. HuntApply acts
                solely as an intermediary in these cases and does not guarantee the quality, availability, or results of
                outfitter services. Any arrangements or agreements made with outfitters are strictly between you and the
                outfitter.</p>

            <hr>

            <h2 id="section4">4. Hunting Application Processing</h2>

            <h3>4.1 Processing Guarantee Disclaimer</h3>
            <p>HuntApply acts as a service provider to facilitate your hunting applications to state agencies. However,
                HuntApply cannot guarantee that applications will always be successfully processed by the state due to
                factors outside its control, including but not limited to technical issues, incomplete state systems, or
                changes in state policies.</p>

            <h3>4.2 Refunds for Processing Failures</h3>
            <p>If HuntApply fails to process your hunting application due to issues fully under HuntApply’s control, you
                will receive a full refund of the application fees for the unprocessed application(s). However,
                HuntApply shall not be held liable for any missed opportunities, loss of points, or other consequences
                resulting from a failure to process applications.</p>

            <h3>4.3 No Liability for External Issues</h3>
            <p>HuntApply shall not be held responsible for application failures caused by:</p>
            <ul>
                <li>State agency systems being unavailable or rejecting submissions.</li>
                <li>Incorrect or incomplete information provided by the user.</li>
                <li>Payment issues related to third-party payment processors or state systems.</li>
            </ul>

            <hr>

            <h2 id="section5">5. Refund Policy</h2>

            <h3>5.1 Application Fees</h3>
            <p>Refunds for hunting applications are allowed only until the application is submitted to the state. Once
                the application is submitted, fees are non-refundable except as outlined in Section 4.2.</p>

            <hr>

            <h2 id="section6">6. Limitation of Liability</h2>

            <h3>6.1 No Liability for Technical Failures</h3>
            <p>HuntApply shall not be held liable for any technical failures, errors, interruptions, or outages of the
                system or services.</p>

            <h3>6.2 No Liability for Results</h3>
            <p>HuntApply shall not be liable for the failure to secure a hunting permit, regardless of the draw odds
                displayed in the application.</p>

            <h3>6.3 No Liability for Data Breaches</h3>
            <p>While HuntApply employs industry-standard encryption to secure sensitive data, we cannot guarantee that
                the system will not be compromised. By using HuntApply, you assume all risk of potential unauthorized
                access to your data.</p>

            <hr>

            <h2 id="section7">7. User Responsibilities</h2>

            <h3>7.1 Accurate Information</h3>
            <p>You are responsible for providing accurate, complete, and up-to-date information when using HuntApply.
                HuntApply is not responsible for errors resulting from incorrect or incomplete user input.</p>

            <h3>7.2 Legal Compliance</h3>
            <p>You are responsible for ensuring that your hunting applications comply with all applicable state and
                federal laws.</p>

            <h3>7.3 Choosing Hunting Outfitters</h3>
            <p>If you choose to use the services of a hunting outfitter or guide, you acknowledge and agree that
                HuntApply is not responsible for the outfitter’s services, actions, or handling of your data once it has
                been shared with them.</p>

            <hr>

            <h2 id="section8">8. Indemnification</h2>

            <p>You agree to indemnify, defend, and hold harmless HuntApply, its officers, directors, employees, and
                agents from and against all claims, damages, losses, liabilities, costs, and expenses (including
                reasonable attorney’s fees) arising from your use of the service or any breach of these Terms and
                Conditions.</p>

            <hr>

            <h2 id="section9">9. Dispute Resolution</h2>

            <h3>9.1 Governing Law</h3>
            <p>These Terms and Conditions are governed by and construed in accordance with the laws of the State of
                Utah, without regard to conflict of law principles.</p>

            <h3>9.2 Arbitration</h3>
            <p>Any disputes arising under these Terms and Conditions shall be resolved through binding arbitration in
                Orem, Utah, in accordance with the rules of the American Arbitration Association.</p>

            <hr>

            <h2 id="section10">10. Intellectual Property</h2>

            <p>All content, logos, designs, and software within HuntApply are the property of HuntApply LLC and are
                protected by applicable intellectual property laws. Unauthorized use of HuntApply’s intellectual
                property is strictly prohibited.</p>

            <hr>

            <h2 id="section11">11. Disclaimer of Warranties</h2>

            <p>HuntApply provides its services "as is" and "as available," without warranties of any kind, express or
                implied, including but not limited to warranties of merchantability, fitness for a particular purpose,
                or non-infringement.</p>

            <hr>

            <h2 id="section12">12. Termination</h2>

            <p>HuntApply reserves the right to terminate or suspend your access to the services at any time, with or
                without cause, including but not limited to violations of these Terms and Conditions.</p>

            <hr>

            <h2 id="section13">13. Severability</h2>

            <p>If any provision of these Terms and Conditions is found to be invalid, illegal, or unenforceable, the
                remaining provisions shall remain in full force and effect.</p>

            <hr>

            <h2 id="section14">14. Force Majeure</h2>

            <p>HuntApply shall not be liable for any failure or delay in performing its obligations due to causes beyond
                its reasonable control, including but not limited to acts of God, natural disasters, war, terrorism,
                government restrictions, labor disputes, or technical failures.</p>

            <hr>

            <h2 id="section15">15. Entire Agreement</h2>

            <p>These Terms and Conditions, along with our Privacy Policy, constitute the entire agreement between you
                and HuntApply concerning the use of the services. They supersede all prior agreements, communications,
                and understandings.</p>

            <p>These Terms and Conditions, along with our Privacy Policy, constitute the entire agreement between you
                and HuntApply concerning the use of the services. They supersede all prior agreements, communications,
                and understandings.</p>
                <button @click="dismissModal" class="done-button">Done</button>
    </div>
  </div>
</template>

<script>
export default {
  name: "TermsAndConditionsView",
  methods: {
    closeOnOverlayClick(event) {
      // If the user clicked the overlay (not the modal content), emit "close"
      if (event.target.classList.contains("terms-modal")) {
        this.dismissModal();
      }
    },
    dismissModal() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.terms-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  max-width: 600px;
  max-height: 80%;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.done-button {
  display: block;
  width: 100%;
  margin-top: 20px;
  padding: 10px;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  text-align: center;
}

.done-button:hover {
  background-color: #0056b3;
}
</style>